import React, { useState, useEffect, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import Lottie from "lottie-react";
import axios from "axios";

import { LoggerContext } from "../../../loggers";

import Template from "../template/index";

import astronaut from "../../../animations/baby_astronaut.json";
import { usersAuthLog } from "../../../utils/loginLogs";
import Button from "../../Button/Button";
import starIcon from "../../../assests/stylumia-stars.svg";
import showPasswordIcon from "../../../assests/showPassword.svg";
import showPasswordEnabledIcon from "../../../assests/show-password-enabled.svg";

import {
  MAGIC,
  MIXPANEL_MAGIC_LINK_EVENT,
  MIXPANEL_MAGIC_LINK_PAGE_NAME,
  MIXPANEL_WITH_PASSWORD_EVENT,
  MIXPANEL_WITH_PASSWORD_PAGE_NAME,
  PASSWORD
} from "../../../loggers/constant";
import MailSVG from "../../../assests/mail.svg";
import RedirectToBlog from "../RedirectToBlog";
import { useDeviceIdentification } from "../../../hooks/useDeviceIdentification";
import Input from "../../Input";
import get_host from "../../../utils";
import {
  AUTH_CONSTANTS,
  login_endpoint,
  redirectMain,
  password_endpoint
} from "../../../constants/constants";
import LockIcon from "../../../assests/lock.png";
import starsIcons from "../../../assests/stars.svg";

function UnifiedLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const logger = useContext(LoggerContext);

  // Shared state
  const [email, setEmail] = useState("");
  const [error, setError] = useState({ email: false, text: false });
  const [finalError, setFinalError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMessage, setErrMessage] = useState("");
  const [internalServerError, setInternalServerError] = useState(false);
  const [mainLoading, setMainLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Magic link specific
  const [emailSent, setEmailSent] = useState(false);
  const [timer, setTimer] = useState(30);
  const [fingerID, setFingerID] = useState("");

  // Device ID hook
  const { getVisitorId } = useDeviceIdentification();

  // Read mode from query param, default to "magic"
  const getModeFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const mode = params?.get("mode");
    return mode === PASSWORD ? PASSWORD : MAGIC;
  };

  const [mode, setMode] = useState(getModeFromQuery());

  // Keep mode in sync with URL
  useEffect(() => {
    setMode(getModeFromQuery());
  }, [location.search]);

  // On mount: check for logged in, prefill email, etc.
  useEffect(() => {
    logger.mixpanel.pageView(MIXPANEL_MAGIC_LINK_PAGE_NAME);

    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    const user = sessionStorage.getItem("userEmailPrefill");
    const jwtToken = Cookies.get("jwt_session");

    if (jwtToken) {
      setIsLoggedIn(true);
      setMainLoading(false);
    } else if (user) {
      setEmail(user);
      setMainLoading(false);
    } else if (emailParam) {
      setEmail(emailParam);
      setMainLoading(false);
    } else {
      setTimeout(() => setMainLoading(false), 800);
    }
    // eslint-disable-next-line
  }, []);

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.keyCode === 13) {
      if (mode === MAGIC && email !== "") {
        handleMagicLinkSubmit();
      } else if (mode === PASSWORD && email !== "" && password !== "") {
        handlePasswordSubmit();
      }
    }
  };

  // Handle input change
  const handleEmailInput = ({ value }) => {
    setEmail(value);
    setErrMessage("");
  };

  // Handle error from Input
  const handleError = (errorState, er) => {
    setError((prev) => {
      const updated = { ...prev, [errorState]: er };
      setFinalError(Object.values(updated).some(Boolean));
      return updated;
    });
  };

  // Magic link submit
  const handleMagicLinkSubmit = async () => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect") || redirectMain;

    sessionStorage.setItem("userEmailId", email);
    setLoading(true);

    try {
      const visitorId = await getVisitorId();
      localStorage.setItem("fingerId", visitorId);

      if (visitorId && email) {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("redirect", redirect);
        formData.append("fingerprint", visitorId);

        usersAuthLog({
          email,
          serviceCategory: "Auth",
          service: "Login",
          eventCategory: "Passwordless Login",
          eventAction: "Submit",
          eventLabel: "User Entered Details",
          fingerprintID: visitorId,
          eventValue: { email, fingerprint: visitorId }
        });

        setFingerID(visitorId);
        const start = performance.now();
        try {
          const res = await axios.post(get_host(login_endpoint), formData);
          if (res.status === 200) {
            setEmailSent(true);
            setErrMessage("");
            setLoading(false);

            // Timer countdown
            setTimer(30);
            const timerCounter = setInterval(() => {
              setTimer((prev) => {
                if (prev === 1) {
                  clearInterval(timerCounter);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);

            usersAuthLog({
              email,
              serviceCategory: "Auth",
              service: "Login",
              eventCategory: "Passwordless Login",
              eventAction: "Submit",
              eventLabel: "Magic Link Sent",
              fingerprintID: visitorId,
              eventValue: {
                status_code: res.status,
                time_taken: performance.now() - start,
                fingerprint: visitorId
              }
            });
          }
        } catch (err) {
          const err_log_message =
            err.response && err.response.data !== undefined
              ? err.response.data.detail || err.response.data.desccription
              : err.message;
          const status = err.response?.status;
          const authLogObj = {
            email,
            serviceCategory: "Auth",
            service: "Login",
            eventCategory: "Passwordless Login",
            eventAction: "Submit",
            fingerprintID: visitorId,
            eventValue: {
              status_code: status,
              time_taken: performance.now() - start,
              fingerprint: visitorId
            }
          };

          if (status === 500) {
            setInternalServerError(true);
            authLogObj.eventLabel = "Internal Server Error";
          } else if (status === 404) {
            authLogObj.eventLabel = "Invalid Email";
          } else {
            authLogObj.eventLabel = err_log_message;
          }
          usersAuthLog(authLogObj);

          setErrMessage(
            err.response && err.response.data !== undefined
              ? err.response.data.detail || err.response.data.desccription
              : err.message
          );
          setLoading(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      logger.mixpanel.log(!!internalServerError, MIXPANEL_MAGIC_LINK_EVENT, {
        email,
        fingerprintID: fingerID
      });
      setLoading(false);
    }
  };

  // Request link again
  const requestAgain = () => {
    setTimer(30);
    setLoading(false);
    setEmailSent(false);

    if (fingerID !== "") {
      const start = performance.now();
      usersAuthLog({
        email,
        serviceCategory: "Auth",
        service: "Login",
        eventCategory: "Passwordless Login",
        eventAction: "Click",
        eventLabel: "Request Link Again",
        fingerprintID: fingerID,
        eventValue: { time_taken: performance.now() - start }
      });
      handleMagicLinkSubmit();
    }
  };

  // Switch to password mode
  const handleWithPassword = () => {
    if (loading) return;
    sessionStorage.setItem("userEmailId", email);
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    // Switch mode and preserve redirect param
    const newParams = new URLSearchParams(location.search);
    newParams.set("mode", PASSWORD);
    if (redirect) newParams.set("redirect", redirect);
    navigate({ search: newParams.toString() }, { replace: true });
  };

  // Password-specific state
  const [password, setPassword] = useState("");
  const [inputPasswordType, setInputPasswordType] = useState("password");

  // On mode change, reset password state
  useEffect(() => {
    if (mode === PASSWORD) {
      logger.mixpanel.pageView(MIXPANEL_WITH_PASSWORD_PAGE_NAME);
      setPassword("");
      setInputPasswordType("password");
      setErrMessage("");
      setInternalServerError(false);
      setError({ email: false, text: false });
      setFinalError(false);
    }
    // eslint-disable-next-line
  }, [mode]);

  // Show/hide password
  const showPassword = () => {
    if (inputPasswordType === "password") {
      setInputPasswordType("text");
    } else {
      setInputPasswordType("password");
    }
  };

  // Handle password input
  const handlePasswordInput = ({ value }) => {
    setPassword(value);
    setErrMessage("");
  };

  // Handle password login submit
  const handlePasswordSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setErrMessage("");
    setInternalServerError(false);

    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect") || redirectMain;

    try {
      const visitorId = await getVisitorId();
      if (visitorId && email && password) {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("redirect", redirect);
        formData.append("password", password);
        formData.append("fingerprint", visitorId);

        usersAuthLog({
          email,
          serviceCategory: "Auth",
          service: "Login",
          eventCategory: "Login With Password",
          eventAction: "Submit",
          eventLabel: "User Entered Details",
          fingerprintID: visitorId,
          eventValue: { email, fingerprint: visitorId }
        });

        const start = performance.now();
        try {
          const res = await axios.post(get_host(password_endpoint), formData);
          if (res.status === 200) {
            window.location = redirect;
            setLoading(false);

            usersAuthLog({
              email,
              serviceCategory: "Auth",
              service: "Login",
              eventCategory: "Login With Password",
              eventAction: "Submit",
              eventLabel: "Login With Password Successful",
              fingerprintID: visitorId,
              eventValue: {
                status_code: res.status,
                time_taken: performance.now() - start,
                fingerprint: visitorId
              }
            });
          } else {
            setErrMessage(
              res.data !== undefined
                ? res.data.detail || res.data.desccription
                : res.message
            );
            setLoading(false);

            usersAuthLog({
              email,
              serviceCategory: "Auth",
              service: "Login",
              eventCategory: "Login With Password",
              eventAction: "Submit",
              eventLabel:
                res.data !== undefined
                  ? res.data.detail || res.data.desccription
                  : res.message,
              fingerprintID: visitorId,
              eventValue: {
                status_code: res.status,
                time_taken: performance.now() - start,
                fingerprint: visitorId
              }
            });
          }
        } catch (err) {
          const status = err.response?.status;
          let eventLabel = "Unknown Error";
          if (status === 500) {
            setInternalServerError(true);
            eventLabel = "Internal Server Error";
          } else if (status === 417) {
            eventLabel = "First Time Password Reset Required";
          } else if (status === 401) {
            eventLabel = "Invalid Email or Password";
          } else if (status === 429) {
            eventLabel = "Device Limit Reached";
          } else {
            eventLabel =
              err.response && err.response.data !== undefined
                ? err.response.data.detail || err.response.data.desccription
                : err.message;
          }
          usersAuthLog({
            email,
            serviceCategory: "Auth",
            service: "Login",
            eventCategory: "Login With Password",
            eventAction: "Submit",
            eventLabel,
            fingerprintID: visitorId,
            eventValue: {
              status_code: status,
              time_taken: performance.now() - start,
              fingerprint: visitorId
            }
          });
          setErrMessage(
            err.response && err.response.data !== undefined
              ? err.response.data.detail || err.response.data.desccription
              : err.message
          );
          setLoading(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      logger.mixpanel.log(!!internalServerError, MIXPANEL_WITH_PASSWORD_EVENT, {
        email,
        fingerprintID: fingerID
      });
      setLoading(false);
    }
  };

  // Switch to magic link mode
  const handleGoPasswordless = () => {
    if (loading) return;
    // Switch mode and preserve redirect param
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    const newParams = new URLSearchParams(location.search);
    newParams.set("mode", MAGIC);
    if (redirect) newParams.set("redirect", redirect);
    navigate({ search: newParams.toString() }, { replace: true });
  };

  // Handle forgot password
  const handleResetPassword = () => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    navigate(
      {
        pathname: "/login/forgot",
        search: redirect ? `?redirect=${redirect}` : ""
      },
      { replace: false }
    );
  };

  // Password form UI
  const isLoginDisabled = useMemo(
    () => email === "" || loading || error.email,
    [email, loading, error.email]
  );

  const isPasswordLoginDisabled = useMemo(
    () => email === "" || password === "" || loading || error.email,
    [email, password, loading, error.email]
  );

  // Render
  if (isLoggedIn) return <RedirectToBlog />;
  if (mainLoading) return null;

  return (
    <Template>
      {mode === MAGIC ? (
        <div>
          <div className="login-welcome-container" style={{ display: "grid" }}>
            <div className="header-container">
              <span className="login-welcome-text">
                Welcome to Consumer Intelligence
              </span>
              <br />
              <span className="login-welcome-sub-text">
                We suggest using the{" "}
                <span className="font-weight-500">
                  email address you use at work.
                </span>
              </span>
            </div>
          </div>
          <br />
          <div onKeyDown={handleKeyPress}>
            <Input
              className="input.button"
              leftChild={<img alt="" className="icon" src={MailSVG} />}
              label={AUTH_CONSTANTS.EMAIL_INPUT.LABEL}
              placeholder="name@company.com"
              value={email}
              onChange={() => handleEmailInput}
              validation={AUTH_CONSTANTS.EMAIL_INPUT.VALIDATION}
              errorMessage={AUTH_CONSTANTS.EMAIL_INPUT.ERROR_MESSAGE}
              getInputValue={handleEmailInput}
              error={error}
              disabled={!!emailSent}
              handleError={handleError}
              type={AUTH_CONSTANTS.EMAIL_INPUT.TYPE}
              name={AUTH_CONSTANTS.EMAIL_INPUT.NAME}
              autoComplete={AUTH_CONSTANTS.EMAIL_INPUT.AUTO_COMPLETE}
            />
            {errMessage && <small style={{ color: "red" }}>{errMessage}</small>}
            {internalServerError === true && (
              <small style={{ color: "red" }}> Internal server error </small>
            )}
            <input value="" type="password" className="display-none" />
            {emailSent ? (
              <div className="check_email_container">
                <Lottie
                  style={{ height: "111px" }}
                  animationData={astronaut}
                  loop
                />
                <div className="check_email">Check Email!!</div>
                <div className="signed_in_for_two_weeks">
                  Click on the link and stay signed in for 1 week. You'll be
                  logged in on a new tab - feel free to close this tab or check
                  out our latest thought-starters.
                </div>
                <br />
                <div className="signed_in_for_two_weeks">
                  {" "}
                  <b> Note:</b> Check your spam/junk folder if the email doesn't
                  arrive in your inbox.
                </div>
                <br />
                <div className="signed_in_for_two_weeks">
                  {" "}
                  New link in {timer} {timer > 1 ? "secs" : "sec"}{" "}
                  {timer === 0 && (
                    <span
                      onClick={requestAgain}
                      style={{
                        color: "rgba(71, 154, 255, 1)",
                        cursor: "pointer"
                      }}
                    >
                      {" "}
                      Request Now{" "}
                    </span>
                  )}{" "}
                </div>
              </div>
            ) : (
              <>
                <div className="options-buttons-container">
                  <div>
                    {loading ? (
                      <button
                        type="button"
                        className="inactive-button without-password"
                        disabled
                      >
                        Verifying...
                      </button>
                    ) : finalError ? (
                      <button
                        type="button"
                        className={`inactive-button without-password ${
                          error && "cursor-not-allowed"
                        }`}
                        disabled
                      >
                        Sign in with Magic Link
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleMagicLinkSubmit}
                        className={
                          !isLoginDisabled
                            ? "without-password click-ripple-effect"
                            : "inactive-button"
                        }
                        disabled={isLoginDisabled}
                      >
                        Sign in with Magic Link
                      </button>
                    )}
                  </div>
                </div>
                <div className="never-remember-password">
                  <img
                    src={starIcon}
                    alt=""
                    className="never-remember-password-icon"
                  />
                  We'll email you a magic link for a password-free sign in
                </div>
                <div className="divider">
                  <span>OR</span>
                </div>
                <div className="with-password with-password-button-container">
                  <Button
                    className={
                      loading ? "cursor-not-allowed" : "with-password-button"
                    }
                    onClick={handleWithPassword}
                    style={{ backgroundColor: "white" }}
                    label="Sign in manually with password"
                    minWidth="200px"
                    width="100%"
                    textColor={
                      isLoginDisabled ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,1)"
                    }
                    backgroundColor="white"
                    border="1px solid rgba(124, 124, 150, 0.28)"
                    error={false}
                    borderRadius="5px"
                    emailPresent
                    disabledBtn={!!loading}
                  />
                </div>
              </>
            )}
          </div>
          <div className="login-contact-us-container">
            <div>
              New to Stylumia?&nbsp;{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.stylumia.ai/get-a-demo/"
              >
                {" "}
                Sign up for a demo{" "}
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="login-welcome-container" style={{ display: "grid" }}>
            <div className="header-container">
              <span className="login-welcome-text">
                Welcome to Consumer Intelligence
              </span>
              <br />
              <span className="login-welcome-sub-text">
                We suggest using the{" "}
                <span className="font-weight-500">
                  email address you use at work.
                </span>
              </span>
            </div>
          </div>
          <br />
          <div onKeyDown={handleKeyPress}>
            <Input
              className="input.button"
              leftChild={<img alt="" className="icon" src={MailSVG} />}
              label={AUTH_CONSTANTS.EMAIL_INPUT.LABEL}
              placeholder={AUTH_CONSTANTS.EMAIL_INPUT.PLACEHOLDER}
              value={email}
              onChange={() => handleEmailInput}
              validation={AUTH_CONSTANTS.EMAIL_INPUT.VALIDATION}
              errorMessage={AUTH_CONSTANTS.EMAIL_INPUT.ERROR_MESSAGE}
              getInputValue={handleEmailInput}
              type={AUTH_CONSTANTS.EMAIL_INPUT.TYPE}
              name={AUTH_CONSTANTS.EMAIL_INPUT.NAME}
              autoComplete={AUTH_CONSTANTS.EMAIL_INPUT.AUTO_COMPLETE}
              error={error}
              handleError={handleError}
            />
            <Input
              leftChild={<img alt="" className="icon" src={LockIcon} />}
              rightChild={
                <img
                  alt=""
                  onClick={showPassword}
                  className="icon show-password"
                  src={
                    inputPasswordType === "password"
                      ? showPasswordIcon
                      : showPasswordEnabledIcon
                  }
                />
              }
              label={AUTH_CONSTANTS.PASSWORD_INPUT.LABEL}
              placeholder={AUTH_CONSTANTS.PASSWORD_INPUT.PLACEHOLDER}
              value={password}
              onChange={() => handlePasswordInput}
              validation={AUTH_CONSTANTS.PASSWORD_INPUT.VALIDATION}
              errorMessage={AUTH_CONSTANTS.PASSWORD_INPUT.ERROR_MESSAGE}
              getInputValue={handlePasswordInput}
              type={inputPasswordType}
              name={inputPasswordType}
              autoComplete={AUTH_CONSTANTS.PASSWORD_INPUT.AUTO_COMPLETE}
              error={error}
              handleError={handleError}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handlePasswordSubmit();
                }
              }}
            />
            {errMessage && <small style={{ color: "red" }}>{errMessage}</small>}
            {internalServerError === true && (
              <small style={{ color: "red" }}> Internal server error </small>
            )}

            <div className="login-button ">
              {loading ? (
                <button
                  type="button"
                  className="inactive-button"
                  disabled
                  style={{ backgroundColor: "#479AFF" }}
                >
                  Logging in...
                </button>
              ) : finalError ? (
                <button type="button" className="inactive-button" disabled>
                  Login
                </button>
              ) : (
                <button
                  type="button"
                  className={
                    isPasswordLoginDisabled
                      ? "inactive-button"
                      : "without-password click-ripple-effect"
                  }
                  disabled={isPasswordLoginDisabled}
                  onClick={handlePasswordSubmit}
                >
                  Login
                </button>
              )}
            </div>
            <div className="forgot-password">
              <span
                className={`show-password ${loading && "cursor-not-allowed"}`}
                onClick={handleResetPassword}
              >
                Forgot password
              </span>
            </div>

            <button
              type="button"
              disabled={!!loading}
              onClick={handleGoPasswordless}
              className={
                loading ? "inactive-button-password" : "go-passwordless"
              }
            >
              <img style={{ marginRight: "10px" }} src={starsIcons} alt="" />
              Go Passwordless
              <span> Never remember a password again</span>
            </button>
          </div>
          <div className="login-contact-us-container">
            <div>
              New to Stylumia?&nbsp;{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.stylumia.ai/get-a-demo/"
              >
                {" "}
                Sign up for a demo{" "}
              </a>
            </div>
          </div>
        </div>
      )}
    </Template>
  );
}

export default UnifiedLogin;
